import { APP_VERSION } from "@/versionInfo";
import { AnalyticsService } from "./AnalyticsService";
import { NoopProvider } from "./providers/NoopProvider";
import { ConsoleProvider } from "./providers/ConsoleProvider";
import { decideAnalyticsEnabled } from "./decideAnalyticsEnabled";
import {
  readAnalyticsOverride,
  readCachedAnalyticsEnabled,
} from "@/Service/AnalyticsToggle";

/**
 * Singleton analytics accessor.
 *
 * The service is constructed synchronously with a NoopProvider so any module
 * that imports `getAnalytics()` at load time gets a real, callable object.
 * The provider is later swapped in-place by bootstrapAnalytics() once the
 * async PostHog SDK finishes loading — buffered events replay in order.
 *
 * This shape guarantees:
 *   - No duplicate initialization (single module-level instance)
 *   - No import-order footguns (the service exists before any bootstrap runs)
 *   - No blocking calls (bootstrapAnalytics is fire-and-forget)
 */
// ready:false marks this as the pre-bootstrap placeholder — events emitted
// before the real provider loads are buffered (bounded) and replayed on swap.
const analytics = new AnalyticsService(new NoopProvider({ ready: false }));

let bootstrapPromise = null;

/**
 * @returns {AnalyticsService}
 */
export const getAnalytics = () => analytics;

/**
 * Kick off provider initialization. Idempotent: repeat calls return the same
 * promise. Safe to invoke on every render — App.js does so inside useEffect.
 *
 * @returns {Promise<AnalyticsService>}
 */
export const bootstrapAnalytics = () => {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = _bootstrap().catch((error) => {
    // Never leave the app in a failed-init state — fall back to Noop so
    // subsequent identify()/track() calls remain safe.
    // eslint-disable-next-line no-console
    console.error("[analytics] bootstrap failed", error);
    analytics.setProvider(new NoopProvider());
    return analytics;
  });
  return bootstrapPromise;
};

const _bootstrap = async () => {
  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
  const debug = process.env.REACT_APP_ANALYTICS_DEBUG === "1";
  const sessionReplay = process.env.REACT_APP_POSTHOG_SESSION_REPLAY !== "0";
  // PostHog free tier = 1 project across all deploy targets. We tag every
  // event with the `environment` super property so a single PostHog project
  // can host dev / dev_consignment / prod_bloomberg / prod_consignment
  // events side-by-side and be filtered per-env in Insights.
  const environment = process.env.REACT_APP_POSTHOG_ENV || "unknown";

  // Super properties: stamped by PostHog's register() onto every event
  // (including session replay ingestion) via localStorage. Prefer this over
  // setPersonProperties for stable environmental facts because events become
  // filterable in Insights without a person-property join.
  analytics.registerSuperProperties({
    environment,
    frontendVersion: APP_VERSION.version,
    commitSha: APP_VERSION.commitSha,
    buildTime: APP_VERSION.buildTime,
  });

  // Compose the three-tier decision (personal override → server value → env
  // default) synchronously from localStorage caches so the bootstrap doesn't
  // block on an auth-gated fetch. The Redux fetchAnalyticsEnabled thunk
  // refreshes the cache after login for next boot; admins who flip the
  // toggle see a "reload required" prompt in the admin UI.
  const personalOverride = readAnalyticsOverride();
  const serverValue = readCachedAnalyticsEnabled();
  const analyticsEnabled = decideAnalyticsEnabled(
    environment,
    personalOverride,
    serverValue,
  );

  if (!analyticsEnabled) {
    // Env-wide off, or personal override off — never load PostHog. Use
    // Console when debug is requested so devs can watch events locally,
    // otherwise Noop.
    const provider = debug ? new ConsoleProvider() : new NoopProvider();
    analytics.setProvider(provider);
    return analytics;
  }

  if (!apiKey) {
    // Enabled but no key configured for this build — fall back to Console
    // or Noop rather than crashing. Common in local runs where PostHog
    // isn't set up but a dev has flipped their personal override on.
    const provider = debug ? new ConsoleProvider() : new NoopProvider();
    analytics.setProvider(provider);
    return analytics;
  }

  // Lazy import keeps the PostHogProvider (and posthog-js) out of the initial
  // bundle for callers that never enable analytics.
  const { PostHogProvider } = await import("./providers/PostHogProvider");
  const provider = new PostHogProvider();
  await provider.init({ apiKey, host, debug, sessionReplay });
  analytics.setProvider(provider);
  return analytics;
};

// Test-only reset. Not exported from the public index.
export const __resetAnalyticsForTests = () => {
  bootstrapPromise = null;
  analytics.setProvider(new NoopProvider());
  analytics.reset();
};
